import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClerk } from '@clerk/react'

import { ChevronSelectorVertical } from '@/components/Icons'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { UserProfile } from '@/features/sidebar'
import { cn, formatFullName } from '@/utils/common'

import { getUserInitials } from './utils'

const CLERK_MODAL_MAX_HEIGHT = '500px'
const CLERK_SECURITY_TAB_CLICK_DELAY_MS = 100
const VERISTAT_EMAIL_DOMAIN = 'veristat'
const CLERK_SELECTORS = {
  securityNavButton: '.cl-navbarButton__security',
  modalCloseButton: '.cl-modalCloseButton',
} as const

export interface UserProfileCardProps {
  user: UserProfile
  onLogout: () => void
  onSettingsClick: () => void
  isCollapsed?: boolean
}

type UserAvatarProps = {
  avatarUrl: string
  userName: string
  className?: string
  textClassName?: string
}

export const UserAvatar: FC<UserAvatarProps> = ({
  avatarUrl,
  userName,
  className,
  textClassName,
}) => (
  <Avatar
    className={cn('size-10', className)}
    style={{
      border: '1px solid var(--Component-colors-Alpha-alpha-black-10)',
    }}
  >
    {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
    <AvatarFallback className={textClassName}>
      {getUserInitials(userName)}
    </AvatarFallback>
  </Avatar>
)

type StatusIndicatorProps = {
  className?: string
}

const StatusIndicator: FC<StatusIndicatorProps> = ({ className }) => (
  <div
    className={cn('absolute', className)}
    style={{
      top: '30px',
      left: '30px',
      width: '12px',
      height: '12px',
      borderRadius: 'var(--radius-full)',
      border: '1.5px solid var(--Colors-Background-bg-primary)',
      backgroundColor: 'var(--Colors-Foreground-fg-success-secondary)',
    }}
  />
)

type UserMenuContentProps = {
  user: UserProfile
  userName: string
  avatarUrl: string
  handleSettings: () => void
  handleUserProfile: () => void
  handleLogoutClick: () => void
  t: (key: string) => string
}

const UserMenuContent: FC<UserMenuContentProps> = ({
  user,
  userName,
  avatarUrl,
  handleSettings,
  handleUserProfile,
  handleLogoutClick,
  t,
}) => (
  <DropdownMenuContent
    className="min-w-56"
    side="top"
    align="end"
    sideOffset={4}
  >
    <DropdownMenuLabel className="p-0 font-normal">
      <div className="flex items-center gap-2 px-2 py-1.5 text-left text-sm">
        <Avatar
          className="w-8 h-8"
          style={{
            border: '1px solid var(--Component-colors-Alpha-alpha-black-10)',
          }}
        >
          <AvatarImage src={avatarUrl} alt={userName} />
          <AvatarFallback>{getUserInitials(userName)}</AvatarFallback>
        </Avatar>
        <div className="grid flex-1 text-sm leading-tight text-left">
          <span className="font-medium truncate">{userName}</span>
          <span className="text-xs truncate">{user.email}</span>
        </div>
      </div>
    </DropdownMenuLabel>
    <DropdownMenuSeparator />
    <DropdownMenuGroup>
      <DropdownMenuItem onClick={handleUserProfile}>
        {t('sidebar.user.profile')}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleSettings}>
        {t('sidebar.user.settings')}
      </DropdownMenuItem>
    </DropdownMenuGroup>
    <DropdownMenuSeparator />
    <DropdownMenuItem onClick={handleLogoutClick}>
      {t('sidebar.user.logout')}
    </DropdownMenuItem>
  </DropdownMenuContent>
)

export const UserProfileCard = ({
  user,
  onLogout,
  onSettingsClick,
  isCollapsed = false,
}: UserProfileCardProps) => {
  const { t } = useTranslation()
  const clerk = useClerk()
  const [open, setOpen] = useState(false)

  const userName = useMemo(
    () => formatFullName(user.first_name, user.last_name),
    [user]
  )

  const handleSettings = useCallback(() => {
    setOpen(false)
    onSettingsClick()
  }, [onSettingsClick])

  /**
   * Opens Clerk's UserProfile modal with customizations:
   * - Hides Profile/Account tab (navbarButton__account, profilePage__account)
   * - Limits modal height (rootBox, cardBox)
   * - Hides Active Devices section (profileSection__activeDevices)
   * - Hides Password section for veristat domain users (profileSection__password)
   * - Auto-navigates to Security tab via setTimeout workaround
   *
   * Element keys follow Clerk's stable class naming (cl-* prefix removed).
   * See: https://clerk.com/docs/guides/customizing-clerk/appearance-prop/bring-your-own-css
   */
  const handleUserProfile = useCallback(() => {
    setOpen(false)
    const isVeristatUser = user.email
      ?.toLowerCase()
      .includes(VERISTAT_EMAIL_DOMAIN)
    clerk.openUserProfile({
      appearance: {
        elements: {
          navbarButton__account: { display: 'none' },
          profilePage__account: { display: 'none' },
          rootBox: { maxHeight: CLERK_MODAL_MAX_HEIGHT },
          cardBox: { maxHeight: CLERK_MODAL_MAX_HEIGHT, overflow: 'hidden' },
          profileSection__activeDevices: { display: 'none' },
          ...(isVeristatUser && {
            profileSection__password: { display: 'none' },
          }),
        },
      },
    })
    const clickSecurityTab = () => {
      const securityButton = document.querySelector<HTMLButtonElement>(
        CLERK_SELECTORS.securityNavButton
      )
      if (securityButton) {
        securityButton.click()
        document
          .querySelector<HTMLElement>(CLERK_SELECTORS.modalCloseButton)
          ?.blur()
      }
    }
    setTimeout(clickSecurityTab, CLERK_SECURITY_TAB_CLICK_DELAY_MS)
  }, [clerk, user.email])

  const handleLogoutClick = useCallback(() => {
    setOpen(false)
    onLogout()
  }, [onLogout])

  // Collapsed view - only show avatar with tooltip
  if (isCollapsed) {
    return (
      <div className="flex-shrink-0">
        <div className="flex justify-center pt-2 border-t border-secondary">
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-0 transition-opacity bg-transparent border-none cursor-pointer hover:opacity-80"
                    aria-label={t('sidebar.user.menu')}
                  >
                    <div className="relative">
                      <UserAvatar
                        avatarUrl={user.avatar_url || ''}
                        userName={userName}
                      />
                      <StatusIndicator />
                    </div>
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <span>{userName}</span>
              </TooltipContent>
            </Tooltip>
            <UserMenuContent
              user={user}
              userName={userName}
              avatarUrl={user.avatar_url || ''}
              handleSettings={handleSettings}
              handleUserProfile={handleUserProfile}
              handleLogoutClick={handleLogoutClick}
              t={t}
            />
          </DropdownMenu>
        </div>
      </div>
    )
  }

  // Expanded view - show full profile card
  return (
    <div className="flex-shrink-0">
      <div className="flex items-start justify-start gap-1 pt-5 pl-2 border-t border-secondary">
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'w-full flex items-center cursor-pointer border-none bg-transparent',
                'transition-colors duration-200',
                'rounded-md p-0 gap-2'
              )}
              aria-label={t('sidebar.user.menu')}
              aria-haspopup="true"
              aria-expanded={open}
            >
              <div className="relative flex-shrink-0">
                <UserAvatar
                  avatarUrl={user.avatar_url || ''}
                  userName={userName}
                />
                <StatusIndicator />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-semibold truncate text-primary-900">
                  {userName || 'User'}
                </div>
                <div className="text-sm font-normal truncate text-tertiary-600">
                  {user?.email || ''}
                </div>
              </div>
              <div className="flex-shrink-0 p-1 text-secondary-700">
                <ChevronSelectorVertical />
              </div>
            </button>
          </DropdownMenuTrigger>
          <UserMenuContent
            user={user}
            userName={userName}
            avatarUrl={user.avatar_url || ''}
            handleSettings={handleSettings}
            handleUserProfile={handleUserProfile}
            handleLogoutClick={handleLogoutClick}
            t={t}
          />
        </DropdownMenu>
      </div>
    </div>
  )
}
